import type { CpmState } from '../../types/centralProductMaster.types';
import {
    REPORT_CATALOG_CATEGORIES,
    type CatalogListRow,
    type ReportCatalogCategory,
} from '../../types/reportsAdmin.types';

export const SEARCH_MIN_CHARS = 2;

export function searchQueryFrom(value: string): string {
    const trimmed = value.trim();
    return trimmed.length >= SEARCH_MIN_CHARS ? trimmed : '';
}

export function stateName(state: CpmState): string {
    return state.name || state.state || '';
}

export function encodeLicence(state: string, code: string): string {
    return `${state}::${code}`;
}

export function decodeLicence(value: string): { state: string; code: string } {
    const [state, code] = value.split('::');
    return { state: state ?? '', code: code ?? '' };
}

export function licenceOptionsForStates(
    states: CpmState[],
    selectedStateNames: string[]
): { options: { value: string; label: string }[]; emptyNote: boolean } {
    const picked = selectedStateNames.length
        ? states.filter((state) => selectedStateNames.includes(stateName(state)))
        : states;
    const seen = new Map<string, string>();
    let emptyNote = false;
    picked.forEach((state) => {
        const types = state.licenseTypes ?? [];
        if (types.length === 0) emptyNote = true;
        types.forEach((licence) => {
            const value = encodeLicence(stateName(state), licence.code);
            if (!seen.has(value)) {
                seen.set(value, `${licence.label} · ${stateName(state)}`);
            }
        });
    });
    return {
        options: Array.from(seen.entries()).map(([value, label]) => ({ value, label })),
        emptyNote,
    };
}

export function canTurnOn(row: {
    kind: CatalogListRow['kind'];
    generatorBound: boolean;
    activeStatus: CatalogListRow['activeStatus'];
}): boolean {
    return row.kind === 'GENERATED' && row.generatorBound && row.activeStatus === 'Active';
}

export function groupByCategory<T extends { categories: ReportCatalogCategory[] }>(
    rows: T[]
): { name: string; rows: T[] }[] {
    const groups: { name: string; rows: T[] }[] = [];
    REPORT_CATALOG_CATEGORIES.forEach((category) => {
        const inGroup = rows.filter((row) => row.categories.includes(category));
        if (inGroup.length) groups.push({ name: category, rows: inGroup });
    });
    const leftover = rows.filter((row) => row.categories.length === 0);
    if (leftover.length) groups.push({ name: 'Uncategorized', rows: leftover });
    return groups;
}

export function shortReportId(id: string): string {
    if (id.length <= 12) return id;
    return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

export function copyTemplateName(name: string): string {
    return name.startsWith('Copy of ') ? `${name} 2` : `Copy of ${name}`;
}

export function plural(count: number, word: string, pluralForm?: string): string {
    const form = count === 1 ? word : (pluralForm ?? `${word}s`);
    return `${count} ${form}`;
}

export function formatToggleAt(iso?: string | null): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

export function formatLastToggleLabel(iso?: string | null): string {
    if (!iso) return 'No toggle recorded';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'No toggle recorded';
    const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
    if (days < 1) return 'Last toggle today';
    if (days === 1) return 'Last toggle 1 day ago';
    return `Last toggle ${days} days ago`;
}
